import { Share2, MessageCircle, Send, Download } from 'lucide-react';
import { toast } from 'sonner';

const canNativeShare = () => typeof navigator !== 'undefined' && !!navigator.share;
const canShareFiles = () => typeof navigator !== 'undefined' && !!navigator.canShare;

export async function shareFile({ blob, fileName, title, text }) {
  if (canShareFiles()) {
    const file = new File([blob], fileName, { type: blob.type });
    const shareData = { title, text, files: [file] };
    try {
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return true;
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('Share failed:', e);
    }
  }
  return false;
}

export async function shareNativeText({ title, text, url }) {
  if (canNativeShare()) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('Share failed:', e);
    }
  }
  return false;
}

function whatsappUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

function messengerUrl(text) {
  return `https://m.me/?text=${encodeURIComponent(text)}`;
}

export function ShareButtonRow({ onDownload, shareText, label = "Partager", compact = false, onShareNative }) {
  const handleWhatsApp = () => {
    window.open(whatsappUrl(shareText), '_blank');
  };

  const handleMessenger = () => {
    window.open(messengerUrl(shareText), '_blank');
  };

  const handleNativeShare = async () => {
    if (onShareNative) {
      await onShareNative();
      return;
    }
    const ok = await shareNativeText({ title: 'La Meute', text: shareText });
    if (!ok) {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success('Texte copie dans le presse-papier');
      } catch {
        toast.error('Partage non supporte sur ce navigateur');
      }
    }
  };

  if (compact) {
    return (
      <div className="flex gap-1.5">
        {onDownload && (
          <button onClick={onDownload} className="p-2 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" title="Telecharger" data-testid="share-download">
            <Download className="w-4 h-4" />
          </button>
        )}
        <button onClick={handleWhatsApp} className="p-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg text-[#25D366] hover:bg-[#25D366]/20 transition-colors" title="WhatsApp" data-testid="share-whatsapp">
          <MessageCircle className="w-4 h-4" />
        </button>
        <button onClick={handleMessenger} className="p-2 bg-[#0084FF]/10 border border-[#0084FF]/30 rounded-lg text-[#0084FF] hover:bg-[#0084FF]/20 transition-colors" title="Messenger" data-testid="share-messenger">
          <Send className="w-4 h-4" />
        </button>
        {canNativeShare() && (
          <button onClick={handleNativeShare} className="p-2 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg text-[#FF6B35] hover:bg-[#FF6B35]/20 transition-colors" title="Partager" data-testid="share-native">
            <Share2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="share-buttons">
      {onDownload && (
        <button onClick={onDownload} className="w-full flex items-center gap-3 p-3 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg text-white hover:border-zinc-500 transition-colors" data-testid="share-download">
          <Download className="w-5 h-5 text-[#FF6B35]" />
          <span className="text-sm font-medium">Telecharger</span>
        </button>
      )}
      <button onClick={handleWhatsApp} className="w-full flex items-center gap-3 p-3 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg text-white hover:bg-[#25D366]/20 transition-colors" data-testid="share-whatsapp">
        <MessageCircle className="w-5 h-5 text-[#25D366]" />
        <span className="text-sm font-medium">Envoyer par WhatsApp</span>
      </button>
      <button onClick={handleMessenger} className="w-full flex items-center gap-3 p-3 bg-[#0084FF]/10 border border-[#0084FF]/30 rounded-lg text-white hover:bg-[#0084FF]/20 transition-colors" data-testid="share-messenger">
        <Send className="w-5 h-5 text-[#0084FF]" />
        <span className="text-sm font-medium">Envoyer par Messenger</span>
      </button>
      {canNativeShare() && (
        <button onClick={handleNativeShare} className="w-full flex items-center gap-3 p-3 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg text-white hover:bg-[#FF6B35]/20 transition-colors" data-testid="share-native">
          <Share2 className="w-5 h-5 text-[#FF6B35]" />
          <span className="text-sm font-medium">Autres apps...</span>
        </button>
      )}
    </div>
  );
}
